import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../App';
import AddEventModal from './AddEventModal';
import EventTable from './EventTable';
import FilterPanel from './FilterPanel';

const fetchAllPredictHQEvents = async (filters) => {
  const params = new URLSearchParams({
    startDate: filters.startDate,
    endDate: filters.endDate,
    minAttendance: String(Math.max(200, filters.minAttendance))
  });
  const endpoint = `/api/predicthq/events?${params}`;
  console.info('[PredictHQ] requesting through Pages Function', { url: endpoint });
  const response = await fetch(endpoint);
  const responseText = await response.text();
  console.info('[PredictHQ] Pages Function response', {
    status: response.status,
    contentType: response.headers.get('content-type'),
    bodyPreview: responseText.slice(0, 500)
  });
  let payload;
  try {
    payload = JSON.parse(responseText);
  } catch {
    throw new Error('The Pages Function returned invalid JSON. Check the deployment logs.');
  }
  if (!response.ok) {
    const failure = new Error(payload.error || `Pages Function returned ${response.status}: ${payload.details || ''}`);
    failure.keyExpired = Boolean(payload.keyExpired);
    throw failure;
  }
  return payload.results || [];
};

const toDatabaseRow = ({ id, title, category, start_local, true_end, end_local, predicted_end_local, check_timings, phq_attendance, attendance, venue_id, venue_name, postcode, venue_formatted_address, lat, lon, labels, icon, size, leaving_start, leaving_end, flag }) => ({ id, title, category, start_local, true_end, end_local, predicted_end_local, check_timings, phq_attendance, attendance, venue_id, venue_name, postcode, venue_formatted_address, lat, lon, labels: labels || [], icon, size, leaving_start, leaving_end, flag });

const toLabelArray = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
  return [];
};

const ICON_BY_LABEL = {
  soccer: '⚽', football: '⚽', rugby: '🏉', cricket: '🏏', tennis: '🎾', golf: '⛳',
  boxing: '🥊', 'mixed-martial-arts': '🥋', 'auto-racing': '🏎', 'horse-racing': '🏇',
  cycling: '🚴', swimming: '🏊', marathon: '🏃', running: '🏃', basketball: '🏀',
  baseball: '⚾', hockey: '🏒', 'american-football': '🏈', esports: '🎮',
  concert: '🎤', comedy: '🎭', theatre: '🎭', opera: '🎼', ballet: '🩰', dance: '💃',
  film: '🎬', exhibition: '💼', conference: '💼', convention: '💼', festival: '🎪',
  food: '🍽', beer: '🍺', family: '👨‍👩‍👧‍👦', charity: '🤝', religious: '🕌',
  market: '🛍', nightlife: '🌙'
};

const ICON_BY_CATEGORY = {
  sports: '🏟', concerts: '🎤', 'performing-arts': '🎭', festivals: '🎪',
  expos: '💼', conferences: '💼', community: '📍'
};

const pickIcon = (category, labels, title = '') => {
  const normalizedLabels = toLabelArray(labels);
  const label = normalizedLabels.find((item) => ICON_BY_LABEL[item]);
  if (label) return ICON_BY_LABEL[label];

  const text = String(title).toLowerCase();
  if (/\bfootball|soccer|\bfc\b|\bvs\.?\b/.test(text)) return '⚽';
  if (/\bconcert|live music\b/.test(text)) return '🎤';
  if (/\bcomedy|stand-up\b/.test(text)) return '🎭';
  if (/\bmarket\b/.test(text)) return '🛍';

  return ICON_BY_CATEGORY[String(category || '').toLowerCase()] || '🎫';
};

const cleanSportTitle = (title, category) => {
  if (String(category || '').toLowerCase() !== 'sports') return title;

  const text = String(title || '').trim();
  const vsIndex = text.search(/\bvs\.?\b/i);
  if (vsIndex === -1) return text;

  const beforeVs = text.slice(0, vsIndex);
  const separatorMatches = [...beforeVs.matchAll(/\s[-–—]\s/g)];
  const separator = separatorMatches.at(-1);
  if (!separator) return text;

  return text.slice(separator.index + separator[0].length).trim() || text;
};

const classifySize = (attendance) => {
  if (attendance < 2000) return 'local';
  if (attendance < 10000) return 'event';
  if (attendance < 50000) return 'main';
  return 'major';
};

const addMinutes = (iso, minutes) => new Date(new Date(iso).getTime() + minutes * 60000).toISOString();

const getLeavingWindow = (size, end) => {
  const windows = { local: [-5, 10], event: [-10, 15], main: [-15, 20], major: [-20, 30] };
  const [before, after] = windows[size] || [0, 0];
  return { leaving_start: addMinutes(end, before), leaving_end: addMinutes(end, after) };
};

const hasTimingFlag = (startIso, endIso) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  return start.getUTCHours() === 0
    || start.getUTCHours() === 23
    || start.toISOString() === end.toISOString();
};

const londonDate = (iso) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}).format(new Date(iso));

const LONDON_CLOCK = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

// London wall-clock time as a UTC instant; start_local is stored in UTC and a London day is 23h or 25h at the BST changeover.
const londonInstant = (dateString, timeString = '00:00') => {
  const guess = new Date(`${dateString}T${timeString}:00Z`);
  const parts = Object.fromEntries(LONDON_CLOCK.formatToParts(guess).map((part) => [part.type, part.value]));
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return new Date(guess.getTime() - (asUtc - guess.getTime()));
};

const londonDayStart = (dateString) => londonInstant(dateString).toISOString();

const londonHhmm = (iso) => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
}).format(new Date(iso));

const addLondonDays = (dateString, count) => {
  const date = new Date(`${dateString}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + count);
  return date.toISOString().slice(0, 10);
};

const nextLondonDay = (dateString) => addLondonDays(dateString, 1);

const getVenue = (row) => {
  const venue = row.venue && typeof row.venue === 'object' ? row.venue : {};
  const address = venue.address && typeof venue.address === 'object' ? venue.address : {};
  const eventAddress = row.address && typeof row.address === 'object' ? row.address : {};
  return {
    id: row.venue_id || venue.id || null,
    name: row.venue_name || venue.name || row.place?.name || '',
    postcode: row.postcode || address.postcode || eventAddress.postcode || '',
    formattedAddress: row.venue_formatted_address || address.formatted_address || address.full_address || eventAddress.formatted_address || ''
  };
};

const transformEvents = (data, startDate, endDate) => {
  const minimumAttendance = 500;
  const output = [];
  data.forEach((row) => {
    const startValue = row.start_local || row.start;
    const endValue = row.predicted_end_local || row.end_local || row.end || startValue;
    const start = new Date(startValue);
    const end = new Date(endValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    if (londonDate(end) < startDate || londonDate(start) > endDate) return;

    const phqAttendance = Number(row.phq_attendance ?? row.attendance ?? 0);
    const labels = toLabelArray(row.phq_labels ?? row.labels);
    const venue = getVenue(row);
    const totalDays = Math.max(1, Math.ceil((end - start) / 86400000));
    const base = {
      id: row.id,
      title: cleanSportTitle(row.title, row.category),
      category: row.category,
      start_local: start.toISOString(),
      true_end: end.toISOString(),
      end_local: row.end || null,
      predicted_end_local: row.predicted_end_local || null,
      check_timings: '',
      phq_attendance: phqAttendance,
      attendance: 0,
      venue_id: venue.id,
      venue_name: venue.name,
      postcode: venue.postcode,
      venue_formatted_address: venue.formattedAddress,
      lat: row.location?.[1] ?? row.lat ?? null,
      lon: row.location?.[0] ?? row.lon ?? null,
      labels,
      icon: pickIcon(row.category, labels, row.title),
      size: '',
      leaving_start: '',
      leaving_end: '',
      flag: false
    };

    const startDay = londonDate(start.toISOString());
    const startTime = londonHhmm(start.toISOString());

    for (let day = 0; day < totalDays; day += 1) {
      const dayDate = addLondonDays(startDay, day);
      const dayStart = londonInstant(dayDate, startTime);
      const dayEnd = new Date(end.getTime() - (totalDays - 1 - day) * 86400000);
      const attendance = Math.floor((phqAttendance / totalDays) / 10) * 10;
      const size = classifySize(attendance);
      const leaving = getLeavingWindow(size, dayEnd.toISOString());
      const [, month, dayOfMonth] = dayDate.split('-');
      const dayRecord = { ...base, id: totalDays > 1 ? `m${dayOfMonth}-${month}_${row.id}` : row.id, start_local: dayStart.toISOString(), true_end: dayEnd.toISOString(), attendance, size, ...leaving };
      if (dayDate >= startDate && dayDate <= endDate) output.push(dayRecord);
    }
  });

  return output.filter((event) => {
    event.check_timings = hasTimingFlag(event.start_local, event.true_end) ? '‼' : '';
    return event.attendance >= minimumAttendance;
  });
};

export default function CreateReport({ filters, setFilters }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loadedRange, setLoadedRange] = useState(null);
  const [draftStatus, setDraftStatus] = useState('Loading draft...');
  const [keyAlert, setKeyAlert] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const saveTimer = useRef(null);

  const draftDays = useMemo(
    () => [...new Set(events.map((event) => londonDate(event.start_local)))].sort(),
    [events]
  );
  const draftDaysKey = draftDays.join(',');

  useEffect(() => {
    setSelectedEventIds([]);
    setSelectedDays([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDaysKey]);

  const toggleEvent = (id) => {
    const eventDay = londonDate(events.find((event) => event.id === id)?.start_local);
    setSelectedDays((current) => current.filter((day) => day !== eventDay));
    setSelectedEventIds((current) => current.includes(id)
      ? current.filter((item) => item !== id)
      : [...current, id]);
  };

  const toggleDay = (day) => {
    const dayIds = events.filter((event) => londonDate(event.start_local) === day).map((event) => event.id);
    const allSelected = dayIds.length > 0 && dayIds.every((id) => selectedEventIds.includes(id));
    setSelectedDays((current) => current.includes(day) || allSelected ? current.filter((item) => item !== day) : [...current, day]);
    setSelectedEventIds((current) => allSelected ? current.filter((id) => !dayIds.includes(id)) : [...new Set([...current, ...dayIds])]);
  };

  const deleteSelectedDraftEvents = () => {
    if (!selectedEventIds.length || !window.confirm(`Delete ${selectedEventIds.length} selected draft event${selectedEventIds.length === 1 ? '' : 's'}?`)) return;
    saveDraft(events.filter((event) => !selectedEventIds.includes(event.id)));
    setSelectedEventIds([]);
    setSelectedDays([]);
  };

  useEffect(() => {
    let cancelled = false;
    const loadDraft = async () => {
      if (!supabase) {
        setDraftStatus('Supabase is not configured');
        return;
      }
      const { data, error } = await supabase.from('temp_events').select('*').order('start_local', { ascending: true });
      if (cancelled) return;
      if (error) {
        console.error('[Draft] load failed', error);
        setDraftStatus(`Draft load failed: ${error.message}`);
        return;
      }
      setEvents(data || []);
      setDraftStatus(data?.length ? `Draft restored (${data.length} events)` : 'Draft empty');
    };
    loadDraft();
    return () => { cancelled = true; if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, []);

  const saveDraft = (nextEvents) => {
    setEvents(nextEvents);
    if (!supabase) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setDraftStatus('Saving draft...');
    saveTimer.current = setTimeout(async () => {
      const { error: deleteError } = await supabase.from('temp_events').delete().not('id', 'is', null);
      if (deleteError) {
        console.error('[Draft] replace delete failed', deleteError);
        setDraftStatus(`Draft save failed: ${deleteError.message}`);
        return;
      }
      if (nextEvents.length) {
        const { error: insertError } = await supabase.from('temp_events').insert(nextEvents.map(toDatabaseRow));
        if (insertError) {
          console.error('[Draft] replace insert failed', insertError);
          setDraftStatus(`Draft save failed: ${insertError.message}`);
          return;
        }
      }
      setDraftStatus(`Draft saved (${nextEvents.length} events)`);
    }, 700);
  };
  const loadEvents = async () => {
    setLoading(true);
    setKeyAlert('');
    try {
      const rawEvents = await fetchAllPredictHQEvents(filters);
      saveDraft(transformEvents(rawEvents, filters.startDate, filters.endDate));
      setLoadedRange({ startDate: filters.startDate, endDate: filters.endDate });
    } catch (error) {
      console.error('[PredictHQ] event load failed', { message: error.message, error, filters });
      if (error.keyExpired) setKeyAlert(error.message);
      else alert(`Could not load PredictHQ events: ${error.message}`);
    } finally { setLoading(false); }
  };

  const upload = async () => {
    if (!supabase) return alert('Supabase is not configured in Pages settings.');
    const publishing = events.filter((event) => selectedEventIds.includes(event.id));
    if (!publishing.length) return alert('Tick at least one event to publish.');
    const days = [...new Set(publishing.map((event) => londonDate(event.start_local)))].sort();
    if (!window.confirm(`Publish ${publishing.length} selected event${publishing.length === 1 ? '' : 's'} across ${days.length} day(s)? Fully selected days replace their current live events; individual selections add only those events.`)) return;
    setLoading(true);
    try {
      for (const day of days) {
        const dayEvents = [...new Map(publishing
          .filter((event) => londonDate(event.start_local) === day)
          .map((event) => [event.id, event])).values()];
        if (selectedDays.includes(day)) {
          const { error: clearError } = await supabase.from('live_events').delete()
            .gte('start_local', londonDayStart(day))
            .lt('start_local', londonDayStart(nextLondonDay(day)));
          if (clearError) throw new Error(`${day}: ${clearError.message}`);
        }
        if (dayEvents.length) {
          const { error: insertError } = await supabase.from('live_events').insert(dayEvents.map(toDatabaseRow));
          if (insertError) throw new Error(`${day}: ${insertError.message}`);
        }
      }
      setDraftStatus(`Published ${publishing.length} selected event(s) to live events`);
      alert(`Published ${publishing.length} selected event${publishing.length === 1 ? '' : 's'} across ${days.length} day(s).`);
    } catch (error) { alert(`Upload failed: ${error.message}`); } finally { setLoading(false); }
  };

  return <div className="report">{keyAlert && <div className="api-key-alert"><strong>PredictHQ key issue</strong><span>{keyAlert}</span><button className="close" onClick={() => setKeyAlert('')}>×</button></div>}<FilterPanel filters={filters} setFilters={setFilters} loadedRange={loadedRange} onLoadEvents={loadEvents} loading={loading} eventCount={events.length} draftStatus={draftStatus} onAddEvent={() => setShowModal(true)} onUpload={upload} publishEventCount={selectedEventIds.length} />{events.length > 0 && <EventTable events={events} selectedEventIds={selectedEventIds} selectedDays={selectedDays} onToggleEvent={toggleEvent} onToggleDay={toggleDay} onDeleteSelected={deleteSelectedDraftEvents} selectionLabel="Publish" onDeleteEvent={(id) => saveDraft(events.filter((event) => event.id !== id))} onEventUpdate={(updated) => saveDraft(events.map((event) => event.id === updated.id ? updated : event))} />}{showModal && <AddEventModal onAdd={(event) => { saveDraft([...events, event]); setShowModal(false); }} onClose={() => setShowModal(false)} />}</div>;
}
